import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Button, IconButton,
  TextField, CircularProgress, Stack, Divider
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Motivos según quién reporta. El cliente reporta al proveedor/servicio; el
// proveedor reporta al cliente (no-show, no da el PIN, etc.).
const MOTIVOS_CLIENTE = [
  'El servicio no se realizó',
  'El proveedor no se presentó',
  'El servicio fue diferente a lo acordado',
  'Comportamiento inapropiado del proveedor',
  'Cobro incorrecto o no autorizado',
  'Otro',
];
const MOTIVOS_PROVEEDOR = [
  'El cliente no se presentó',
  'El cliente no proporcionó el PIN de confirmación',
  'El cliente solicitó algo distinto a lo acordado',
  'Comportamiento inapropiado del cliente',
  'Otro',
];
// Aclaración = servicio YA finalizado. Es un canal de soporte, no congela dinero.
const MOTIVOS_ACLARACION = [
  'El servicio no fue lo acordado',
  'Cobro incorrecto o cargo no reconocido',
  'Problema con mi factura',
  'Calidad del servicio',
  'Otro',
];

const MAX_ARCHIVOS = 5;
const MAX_MB = 5;

// Folio legible derivado del _id (estable y único, sin migración de datos)
const folioDe = (id) => '#' + String(id || '').slice(-6).toUpperCase();

// ---- Barra de progreso de 3 segmentos ----
const Progreso = ({ paso }) => (
  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
    {[1, 2, 3].map((n) => (
      <Box key={n} sx={{
        flex: 1, height: 5, borderRadius: 3,
        bgcolor: paso >= n ? '#FF827A' : '#E2E8F0',
        transition: 'background-color .2s'
      }} />
    ))}
  </Stack>
);

// ---- Tarjeta de motivo (radio custom) ----
const MotivoCard = ({ label, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 1.5, px: 2.5, py: 1.8, mb: 1.5, borderRadius: '14px', cursor: 'pointer',
      border: '1.5px solid', transition: 'all .15s',
      borderColor: selected ? '#FF827A' : '#E2E8F0',
      bgcolor: selected ? '#FFE0DD' : 'white',
      '&:hover': { borderColor: selected ? '#FF827A' : '#CBD5E1' }
    }}
  >
    <Typography sx={{ fontSize: 15, fontWeight: selected ? 700 : 500, color: selected ? '#B23B33' : '#0E2465' }}>
      {label}
    </Typography>
    <Box sx={{
      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
      border: '2px solid', borderColor: selected ? '#FF827A' : '#CBD5E1',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {selected && <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: '#FF827A' }} />}
    </Box>
  </Box>
);

// ---- Fila del resumen (paso 3) ----
const FilaResumen = ({ label, value, alt }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2,
    px: 2.5, py: 1.8, bgcolor: alt ? '#F8FAFC' : 'white',
  }}>
    <Typography sx={{ fontSize: 13.5, color: '#737791', flexShrink: 0 }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0E2465', textAlign: 'right', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const ReporteServicioModal = ({ open, onClose, onConfirm, loading, esProveedor, booking, modo = 'reporte' }) => {
  const [paso, setPaso] = useState(1);
  const [motivo, setMotivo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const esAclaracion = modo === 'aclaracion';
  const MOTIVOS = esAclaracion ? MOTIVOS_ACLARACION : (esProveedor ? MOTIVOS_PROVEEDOR : MOTIVOS_CLIENTE);
  const tituloHeader = esAclaracion ? 'Reportar / Aclaración' : 'Reportar servicio';
  const tituloPaso1 = esAclaracion ? '¿Sobre qué es tu aclaración?' : '¿Por qué quieres reportar este servicio?';

  useEffect(() => {
    if (open) {
      setPaso(1); setMotivo(''); setDescripcion(''); setArchivos([]); setError('');
    }
  }, [open]);

  const atras = () => {
    setError('');
    if (paso === 1) onClose();
    else setPaso((p) => p - 1);
  };

  const continuar = () => {
    setError('');
    if (paso === 1) {
      if (!motivo) return;
      setPaso(2);
    } else if (paso === 2) {
      if (descripcion.trim().length < 10) {
        setError('Describe qué pasó (mínimo 10 caracteres).');
        return;
      }
      setPaso(3);
    }
  };

  const onArchivos = (e) => {
    setError('');
    const nuevos = Array.from(e.target.files || []);
    e.target.value = ''; // permite volver a elegir el mismo archivo
    let acumulado = [...archivos];
    for (const f of nuevos) {
      if (acumulado.length >= MAX_ARCHIVOS) { setError(`Máximo ${MAX_ARCHIVOS} archivos.`); break; }
      if (f.size > MAX_MB * 1024 * 1024) { setError(`Cada archivo debe pesar menos de ${MAX_MB} MB.`); continue; }
      acumulado.push(f);
    }
    setArchivos(acumulado.slice(0, MAX_ARCHIVOS));
  };

  const quitarArchivo = (i) => setArchivos((a) => a.filter((_, idx) => idx !== i));

  const enviar = () => onConfirm({ motivo, descripcion: descripcion.trim(), archivos });

  const textoBoton = paso === 3 ? 'Enviar reporte' : 'Continuar';
  const botonDeshabilitado = loading || (paso === 1 && !motivo);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: '22px' } } }}>
      {/* ---- Header ---- */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
        <IconButton onClick={atras} size="small" sx={{ color: '#737791' }}>
          <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberRoundedIcon sx={{ fontSize: 22, color: '#FF827A' }} />
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#B23B33' }}>{tituloHeader}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#737791' }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: '#F1F5F9' }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Progreso paso={paso} />
        <Typography sx={{ fontSize: 13, color: '#94A3B8', fontWeight: 600, mb: 2.5 }}>
          Paso {paso} de 3
        </Typography>

        {/* ============ PASO 1: MOTIVO ============ */}
        {paso === 1 && (
          <>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0E2465', mb: 0.5 }}>
              {tituloPaso1}
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#737791', mb: 3 }}>
              Selecciona el motivo que mejor describa tu situación.
            </Typography>
            {MOTIVOS.map((m) => (
              <MotivoCard key={m} label={m} selected={motivo === m} onClick={() => setMotivo(m)} />
            ))}
          </>
        )}

        {/* ============ PASO 2: DESCRIPCIÓN + EVIDENCIAS ============ */}
        {paso === 2 && (
          <>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0E2465', mb: 0.5 }}>
              Cuéntanos qué pasó
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#737791', mb: 3 }}>
              Describe con el mayor detalle posible para que podamos ayudarte.
            </Typography>

            <TextField
              fullWidth multiline rows={4}
              placeholder="Describe la situación (mínimo 10 caracteres)"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '14px' } }}
            />

            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0E2465', mb: 1 }}>
              Adjuntar evidencias <span style={{ color: '#94A3B8', fontWeight: 500 }}>(opcional)</span>
            </Typography>

            <Box
              onClick={() => inputRef.current?.click()}
              sx={{
                border: '1.5px dashed #CBD5E1', borderRadius: '14px', px: 2, py: 3,
                textAlign: 'center', cursor: 'pointer', mb: 2,
                '&:hover': { borderColor: '#FF827A', bgcolor: '#FFF5F4' }
              }}
            >
              <CloudUploadOutlinedIcon sx={{ fontSize: 30, color: '#94A3B8', mb: 0.5 }} />
              <Typography sx={{ fontSize: 13.5, color: '#737791' }}>
                Sube fotos o documentos (máx. {MAX_ARCHIVOS}, {MAX_MB} MB c/u)
              </Typography>
              <input
                ref={inputRef} type="file" hidden multiple
                accept="image/*,application/pdf"
                onChange={onArchivos}
              />
            </Box>

            {archivos.map((f, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, mb: 1,
                borderRadius: '10px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9'
              }}>
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: '#7495F8' }} />
                <Typography noWrap sx={{ flex: 1, fontSize: 13, color: '#0E2465' }}>{f.name}</Typography>
                <IconButton size="small" onClick={() => quitarArchivo(i)} sx={{ color: '#B23B33' }}>
                  <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))}
          </>
        )}

        {/* ============ PASO 3: REVISIÓN ============ */}
        {paso === 3 && (
          <>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0E2465', mb: 0.5 }}>
              Revisa tu reporte
            </Typography>
            <Typography sx={{ fontSize: 14, color: '#737791', mb: 3 }}>
              Verifica que la información sea correcta antes de enviar.
            </Typography>

            <Box sx={{ borderRadius: '14px', overflow: 'hidden', border: '1px solid #F1F5F9', mb: 3 }}>
              <FilaResumen label="Servicio" value={booking?.servicioNombre || 'Servicio'} alt />
              <FilaResumen label="ID de reserva" value={folioDe(booking?._id)} />
              <FilaResumen label="Tipo" value="Reporte" alt />
              <FilaResumen label="Motivo" value={motivo} />
              <FilaResumen label="Descripción" value={descripcion.trim()} alt />
              <FilaResumen
                label="Evidencias"
                value={archivos.length ? `${archivos.length} archivo${archivos.length > 1 ? 's' : ''} adjuntado${archivos.length > 1 ? 's' : ''}` : 'Sin evidencias'}
              />
            </Box>

            <Box sx={{
              display: 'flex', gap: 1.5, p: 2, borderRadius: '14px',
              bgcolor: '#FFE0DD', border: '1px solid #FF827A'
            }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 22, color: '#B23B33', flexShrink: 0, mt: '2px' }} />
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#B23B33', mb: 0.5 }}>
                  ¿Qué sucede después?
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#B23B33', lineHeight: 1.6 }}>
                  {esAclaracion
                    ? 'Tu aclaración llegará a nuestro equipo de soporte, que revisará el caso y te contactará. El pago del servicio ya fue procesado.'
                    : 'El pago quedará congelado y nuestro equipo revisará el caso. Te notificaremos la resolución por este medio. Si nadie reporta un problema dentro de los 3 días posteriores al servicio, este se completa automáticamente.'}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {error && (
          <Typography sx={{ fontSize: 13, color: '#B23B33', fontWeight: 600, mt: 2 }}>{error}</Typography>
        )}
      </DialogContent>

      {/* ---- Footer ---- */}
      <Box sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          fullWidth variant="contained" disableElevation
          disabled={botonDeshabilitado}
          onClick={paso === 3 ? enviar : continuar}
          sx={{
            bgcolor: '#FF827A', color: 'white', textTransform: 'none', fontWeight: 800,
            borderRadius: '14px', py: 1.6, fontSize: 16,
            '&:hover': { bgcolor: '#e06f68' },
            '&.Mui-disabled': { bgcolor: '#FCA5A5', color: 'white' }
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : textoBoton}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ReporteServicioModal;
